/**
 * SVG Export utilities
 * Generate clean SVG output for laser generator (xTool format)
 */
import type { TemplateShape } from '../types/dbTypes';

/**
 * Export shapes to clean SVG format
 *
 * Output format matches xTool expectations:
 * - SVG with mm units and viewBox
 * - Shapes with vector-effect="non-scaling-stroke"
 * - Graphics embedded as groups
 * - Data attributes for field IDs
 */
export function exportToSvg(
    shapes: TemplateShape[],
    workAreaWidth: number,
    workAreaHeight: number
): string {
    // Sort: border → graphics → rects (bottom to top in SVG terms)
    const sortedShapes = [...shapes].sort((a, b) => {
        const aIsBorder = a.width >= workAreaWidth - 1 && a.height >= workAreaHeight - 1;
        const bIsBorder = b.width >= workAreaWidth - 1 && b.height >= workAreaHeight - 1;

        if (aIsBorder && !bIsBorder) return -1;
        if (!aIsBorder && bIsBorder) return 1;
        if (a.type === 'graphic' && b.type !== 'graphic') return -1;
        if (a.type !== 'graphic' && b.type === 'graphic') return 1;
        return a.orderIndex - b.orderIndex;
    });

    let svgContent = '';

    sortedShapes.forEach(shape => {
        if (shape.type === 'rect') {
            const transform = shape.rotation
                ? ` transform="rotate(${shape.rotation}, ${shape.x + shape.width / 2}, ${shape.y + shape.height / 2})"`
                : '';

            const fieldAttr = shape.fieldId ? ` data-field-id="${shape.fieldId}"` : '';

            svgContent += `  <rect${transform} x="${shape.x.toFixed(2)}" y="${shape.y.toFixed(2)}" width="${shape.width.toFixed(2)}" height="${shape.height.toFixed(2)}" fill="none" stroke="#0000FF" stroke-width="1" vector-effect="non-scaling-stroke"${fieldAttr}/>\n`;
        } else if (shape.type === 'graphic' && shape.svgData) {
            // Extract inner content from SVG
            const parser = new DOMParser();
            const doc = parser.parseFromString(shape.svgData, 'image/svg+xml');
            const svg = doc.querySelector('svg');

            if (svg) {
                const innerContent = svg.innerHTML;
                const transform = `translate(${shape.x.toFixed(2)}, ${shape.y.toFixed(2)})` +
                    (shape.rotation ? ` rotate(${shape.rotation}, ${shape.width / 2}, ${shape.height / 2})` : '');

                svgContent += `  <g transform="${transform}" data-shape-id="${shape.id}">\n    ${innerContent}\n  </g>\n`;
            }
        }
    });

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="${workAreaWidth}mm" height="${workAreaHeight}mm" viewBox="0 0 ${workAreaWidth} ${workAreaHeight}">
${svgContent}</svg>`;
}

/**
 * Generate empty SVG with work area dimensions
 */
export function generateEmptySvg(width: number, height: number): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}mm" height="${height}mm" viewBox="0 0 ${width} ${height}"></svg>`;
}
